import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../Navbar";
import "./Boost.css";
import {
  BOOST_MAX_VIEWS,
  BOOST_PRICE,
  createBoostOrder,
  formatDate,
  formatTime,
  getBoostStatus,
  loadRazorpayScript,
  verifyBoostPayment,
} from "../../services/boostApi";
import { RAZORPAY_BRAND } from "../../utils/razorpayBranding";

// =====================================================
// BOOST YOUR BLOG  —  /boost/:blogId
//
// Opened from the 3-dot menu of one of your own blogs. It explains what ₹29
// buys (50 DIFFERENT readers), what the analytics will show, and opens
// Razorpay. Nothing is activated here: the campaign only exists once the
// server has verified the payment, so cancelling or failing simply leaves
// this page as it was.
// =====================================================
function BoostBlog() {
  const { blogId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [blog, setBlog] = useState(null);
  const [boost, setBoost] = useState(null);
  const [paying, setPaying] = useState(false);
  const [notice, setNotice] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getBoostStatus(blogId);
      setBlog(data.blog || null);
      setBoost(data.boost || null);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Unable to open the boost page for this blog."
      );
    } finally {
      setLoading(false);
    }
  }, [blogId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Pre-loaded so the checkout opens on the first tap rather than the second.
  useEffect(() => {
    loadRazorpayScript();
  }, []);

  const isActive = Boolean(boost && boost.isActive);

  // ── PAYMENT ──
  const startPayment = async () => {
    if (paying) return;
    setNotice("");
    setPaying(true);

    try {
      const ready = await loadRazorpayScript();
      if (!ready) {
        setNotice("Payment could not be opened. Please check your connection and try again.");
        setPaying(false);
        return;
      }

      const data = await createBoostOrder(blogId);

      const options = {
        key: data.key_id,
        amount: data.order.amount,
        currency: data.order.currency,
        ...RAZORPAY_BRAND,
        description: `Boost Your Blog — ${BOOST_MAX_VIEWS} users`,
        order_id: data.order.id,

        // ── SUCCESS ──
        handler: async (paymentResponse) => {
          try {
            const verified = await verifyBoostPayment({
              blogId,
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature,
            });

            if (verified?.success && verified.boost) {
              // The success screen is handed the verified campaign, and can
              // also re-read it from the server on a refresh.
              navigate(`/boost/${blogId}/success`, {
                replace: true,
                state: { boost: verified.boost, blog: verified.blog },
              });
              return;
            }

            setNotice(verified?.message || "Payment verification failed. Boost not activated.");
            loadStatus();
          } catch (err) {
            setNotice(
              err?.response?.data?.message ||
                "Payment verification failed. Boost not activated."
            );
            loadStatus();
          } finally {
            setPaying(false);
          }
        },

        theme: { color: "#8b5cf6" },

        // ── CANCEL ── nothing was charged, nothing is activated.
        modal: {
          ondismiss: () => {
            setPaying(false);
            setNotice("Payment cancelled. Your blog was not boosted.");
          },
        },
      };

      const checkout = new window.Razorpay(options);

      // ── FAILURE ── stay on this page, boost untouched.
      checkout.on("payment.failed", (response) => {
        setPaying(false);
        setNotice(
          response?.error?.description || "Payment failed. Your blog was not boosted."
        );
      });

      checkout.open();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.alreadyActive && data.boost) {
        setBoost(data.boost);
        setNotice("This blog already has an active boost.");
      } else {
        setNotice(data?.message || "Something went wrong while opening payment.");
      }
      setPaying(false);
    }
  };

  // ── RENDER ──
  if (loading) {
    return (
      <div className="boost-page">
        <Navbar />
        <div className="boost-container">
          <div className="boost-loading">
            <div className="boost-spinner" />
            <p>Loading boost…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="boost-page">
        <Navbar />
        <div className="boost-container">
          <div className="boost-card boost-error-card">
            <h2>Boost unavailable</h2>
            <p>{error}</p>
            <button type="button" className="boost-ghost-btn" onClick={() => navigate(-1)}>
              ← Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="boost-page">
      <Navbar />

      <div className="boost-container">
        <div className="boost-header">
          <div className="boost-badge">🚀 Boost</div>
          <h1 className="boost-title">Boost Your Blog</h1>
          <p className="boost-sub">
            Put this blog at the top of Read Blogs for {BOOST_MAX_VIEWS} different readers.
          </p>
        </div>

        {/* SELECTED BLOG */}
        {blog && (
          <div className="boost-card boost-blog-card">
            <span className="boost-card-label">Selected blog</span>
            <h2 className="boost-blog-title">{blog.title || "Untitled blog"}</h2>
            <p className="boost-blog-meta">
              by {blog.author} · {formatDate(blog.createdAt)}
            </p>
          </div>
        )}

        {/* WHAT ₹29 BUYS */}
        <div className="boost-card">
          <h3 className="boost-card-heading">What this boost does</h3>
          <ul className="boost-points">
            <li>
              <span className="boost-point-icon">👥</span>
              <span>
                Your blog reaches <strong>{BOOST_MAX_VIEWS} different users</strong> — it appears
                at the <strong>top of Read Blogs</strong> for readers who have not seen it yet.
              </span>
            </li>
            <li>
              <span className="boost-point-icon">1️⃣</span>
              <span>
                Each reader is counted <strong>once</strong>. Refreshing or reopening the app
                never counts twice.
              </span>
            </li>
            <li>
              <span className="boost-point-icon">🛑</span>
              <span>
                The boost <strong>stops automatically</strong> at {BOOST_MAX_VIEWS}/
                {BOOST_MAX_VIEWS}, and the blog returns to the normal feed.
              </span>
            </li>
          </ul>

          <h3 className="boost-card-heading">Boost Analytics includes</h3>
          <div className="boost-chips">
            <span className="boost-chip">👁️ Views</span>
            <span className="boost-chip">📅 Date</span>
            <span className="boost-chip">⏰ Time</span>
          </div>
        </div>

        {/* CURRENT CAMPAIGN */}
        {boost && (
          <div className="boost-card">
            <h3 className="boost-card-heading">
              {isActive ? "Boost is running" : "Last boost"}
            </h3>
            <div className="boost-progress-row">
              <span className={`boost-status ${isActive ? "is-active" : "is-done"}`}>
                {isActive ? "🟢 Active" : "✅ Completed"}
              </span>
              <span className="boost-progress-count">
                {boost.uniqueViews}/{boost.maxViews}
              </span>
            </div>
            <div className="boost-progress-track">
              <div
                className="boost-progress-fill"
                style={{
                  width: `${Math.min(100, (boost.uniqueViews / boost.maxViews) * 100)}%`,
                }}
              />
            </div>
            <p className="boost-meta-line">
              Started {formatDate(boost.startedAt)} at {formatTime(boost.startedAt)}
            </p>
            <button
              type="button"
              className="boost-secondary-btn"
              onClick={() => navigate(`/boost/${blogId}/analytics`)}
            >
              📊 Boost Analytics
            </button>
          </div>
        )}

        {notice && <div className="boost-notice">{notice}</div>}

        {/* PAY */}
        <div className="boost-card boost-pay-card">
          {isActive ? (
            <>
              <p className="boost-pay-note">
                This blog is being boosted right now. You can boost it again once the current
                campaign reaches {boost.maxViews}/{boost.maxViews}.
              </p>
              <button
                type="button"
                className="boost-pay-btn boost-pay-btn-disabled"
                disabled
              >
                ₹{BOOST_PRICE} — Boost Your Blog
              </button>
            </>
          ) : (
            <>
              <div className="boost-price">
                ₹{BOOST_PRICE}
                <span> one time</span>
              </div>
              <button
                type="button"
                className="boost-pay-btn"
                onClick={startPayment}
                disabled={paying}
              >
                {paying ? "Opening payment…" : `₹${BOOST_PRICE} — Boost Your Blog`}
              </button>
              <p className="boost-pay-note">
                🔒 Secure Razorpay payment · Boost activates only after a successful payment
              </p>
            </>
          )}
        </div>

        <button type="button" className="boost-ghost-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>
    </div>
  );
}

export default BoostBlog;
